import { Helmet } from "react-helmet-async";
import { jsonLd } from "@/lib/seoHelpers";

const BASE_URL = "https://www.garden-review.co.uk";

interface BreadcrumbItem {
  name: string;
  url?: string; // URL is optional for the last item (current page)
}

interface BreadcrumbSchemaProps {
  items: BreadcrumbItem[];
}

/**
 * Generates BreadcrumbList structured data for navigation context.
 * Improves SEO by helping Google understand page hierarchy.
 * 
 * Usage:
 * <BreadcrumbSchema items={[
 *   { name: "Home", url: "/" },
 *   { name: "Reviews", url: "/category/reviews" },
 *   { name: "Best Lawn Mowers 2024" }  // last item has no URL
 * ]} />
 */
export const BreadcrumbSchema = ({ items }: BreadcrumbSchemaProps) => {
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      ...(item.url && {
        "item": item.url.startsWith("http") ? item.url : `${BASE_URL}${item.url}`
      })
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {jsonLd(breadcrumbSchema)}
      </script>
    </Helmet>
  );
};

export default BreadcrumbSchema;
